"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Jalon = {
  date: string;
  titre: string;
  texte: string;
  statut: "fait" | "a-venir" | "attendu";
};
const jalons: Jalon[] = [{
  date: "4\u00a0août 2026",
  titre: "Publication du décret n°\u00a02026-728",
  texte: "Décret du 1er\u00a0août 2026 publié au Journal officiel\u00a0: 13\u00a0indicateurs modifiés, un indicateur créé (indicateur\u00a033, propre à l'apprentissage).",
  statut: "fait"
}, {
  date: "1er\u00a0novembre 2026",
  titre: "Entrée en vigueur",
  texte: "Les audits réalisés à compter de cette date portent sur les 33\u00a0indicateurs dans leur nouvelle rédaction, sans mesure transitoire.",
  statut: "a-venir"
}, {
  date: "Date non connue",
  titre: "Guide de lecture (version\u00a010)",
  texte: "Non publié à ce jour. Selon les organismes certificateurs, il s'appliquera aux audits réalisés à compter du 1er\u00a0novembre 2026.",
  statut: "attendu"
}, {
  date: "Date non connue",
  titre: "Textes attendus",
  texte: "Arrêtés fixant les seuils des indicateurs\u00a019 et\u00a020\u00a0; nouvel arrêté relatif aux modalités d'audit\u00a0; mise à jour de l'arrêté relatif à l'accréditation des organismes certificateurs.",
  statut: "attendu"
}];
const pastille: Record<Jalon["statut"], string> = {
  "fait": "bg-[#62751D] border-[#62751D]",
  "a-venir": "bg-[hsl(343,80%,35%)] border-[hsl(343,80%,35%)]",
  "attendu": "bg-white border-[hsl(343,80%,35%)]"
};
export default function WhyChooseEffetConseil() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="pourquoi-faire-appel-à-effet-conseil" className="w-full bg-[#F5EDE8] relative overflow-hidden ![padding:80px_0px_80px_0px]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div ref={ref} className={`max-w-5xl mx-auto text-center transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[hsl(343,80%,35%)] mb-8 leading-tight">
            Qualiopi®&nbsp;: une certification exigeante, des accompagnements
            structurés
          </h2>
          <p className="text-base text-[hsl(210,10%,23%)] font-sans leading-relaxed mb-10">
            La certification Qualiopi® conditionne l'accès des prestataires
            d'actions concourant au développement des compétences aux fonds
            publics et mutualisés (art.&nbsp;L.&nbsp;6316-1 du Code du travail).
            Elle repose sur le Référentiel National Qualité&nbsp;: 7&nbsp;critères
            et, pour les audits réalisés à compter du 1er&nbsp;novembre 2026,
            33&nbsp;indicateurs, dont l'applicabilité dépend de la catégorie
            d'action.
          </p>

          {/* Calendrier du RNQ révisé */}
          <div className="bg-white rounded-xl border border-[#d6cfc4]/60 shadow-sm p-6 md:p-8 mb-4 text-left">
            <h3 className="font-serif text-2xl font-bold text-[hsl(343,80%,35%)] mb-1">
              RNQ révisé&nbsp;: le calendrier
            </h3>
            <p className="text-sm text-[hsl(210,10%,23%)]/70 font-sans mb-6">
              Situation au 5&nbsp;octobre 2026
            </p>
            <ol className="relative border-l-2 border-[hsl(343,80%,35%)]/20 ml-2 space-y-6">
              {jalons.map((jalon, index) => <li key={index} data-index={index} className="pl-6 relative">
                  <span className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${pastille[jalon.statut]}`} aria-hidden="true" />
                  <p className="text-xs font-bold uppercase tracking-wider text-[hsl(343,80%,35%)] mb-1">
                    {jalon.date}
                  </p>
                  <p className="font-serif text-lg font-semibold text-[hsl(210,10%,23%)] mb-1">
                    {jalon.titre}
                  </p>
                  <p className="text-sm leading-relaxed text-[hsl(210,10%,23%)]">
                    {jalon.texte}
                  </p>
                </li>)}
            </ol>
          </div>
          <p className="text-xs text-[hsl(210,10%,23%)]/60 font-sans leading-relaxed mb-6 text-left">
            Décret n°&nbsp;2026-728 du 1er&nbsp;août 2026 (JO du 4&nbsp;août 2026)&nbsp;;
            Code du travail, art.&nbsp;L.&nbsp;6316-1 et R.&nbsp;6316-1.
          </p>
          <p className="mb-10 text-left">
            <Link to="/cadre-juridique-de-la-formation/" className="inline-flex items-center gap-2 font-sans text-base font-semibold text-[hsl(343,80%,35%)] hover:underline underline-offset-4">
              Loi du 25&nbsp;juin 2026 et RNQ&nbsp;: les points de convergence
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </p>

          <p className="text-base text-[hsl(210,10%,23%)] font-sans leading-relaxed mb-6">
            Les audits Qualiopi® exigent une préparation rigoureuse. Effet
            Conseil accompagne les organismes dans la préparation de leurs
            audits, qu'il s'agisse de l'audit initial, de l'audit de
            surveillance ou de l'audit de renouvellement. L'approche repose sur
            la vulgarisation des exigences, la co-construction des outils et la
            montée en compétences de vos équipes.
          </p>
          <p className="text-base text-[hsl(210,10%,23%)] font-sans leading-relaxed font-bold mb-10">
            L'engagement d'Effet Conseil&nbsp;: vous permettre d'aborder vos
            audits avec sérénité, avec un système qualité documenté, approprié
            par vos équipes et orienté vers l'amélioration continue.
          </p>

          {/* Indépendance conseil / audit */}
          <div className="bg-white rounded-xl border-l-4 border-[#62751D] shadow-sm p-6 md:p-8 text-left">
            <h3 className="font-serif text-xl font-bold text-[#62751D] mb-3">
              Conseil et audit&nbsp;: deux activités séparées
            </h3>
            <p className="text-base leading-relaxed text-[hsl(210,10%,23%)]">
              Christelle CHARRIER-ROBELIN réalise également des audits
              Qualiopi® pour le compte d'organismes certificateurs. Ces deux
              activités sont séparées&nbsp;: elle n'audite pas un organisme
              qu'elle accompagne ou a accompagné. La décision de certification
              appartient à l'organisme certificateur.
            </p>
          </div>
        </div>
      </div>
    </section>;
}